import React, { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CoworkerGroupBannerBar, CoworkerGroupBannerClear, CoworkerGroupBannerText } from './styles';
import { useActiveCoworkerGroup } from '../hooks/use-active-coworker-group.hook';
import { useFloorplanMapDate } from '../hooks/use-floorplan-map-date.hook';
import { useCoworkerAttendanceQuery } from 'data/queries/useCoworkerAttendanceQuery.hook';
import { CoworkerGroupResponse } from 'store/api-client/coworker-types';

interface CoworkerGroupBannerProps {
  group: CoworkerGroupResponse;
}

const bold = (chunks: ReactNode[]): JSX.Element => <strong>{chunks}</strong>;

/**
 * Focus mode's banner over the map (§C3, CV-TEAM-05): which group the plan is filtered to, how many
 * of them are in the building that day, and the way out. The count comes from the attendance endpoint
 * scoped to the group — the whole building, not just this floor — so it matches the sheet's numbers.
 * On a weekend the endpoint has no entry for the day, and the banner simply names the group.
 */
export const CoworkerGroupBanner = ({ group }: CoworkerGroupBannerProps): JSX.Element => {
  const intl = useIntl();
  const { date, getToday } = useFloorplanMapDate();
  const { clearActiveGroup } = useActiveCoworkerGroup();
  const { day, attendance } = useCoworkerAttendanceQuery({ date, groupUuid: group.uuid });

  const isToday = date === getToday();
  const formattedDate = useMemo(
    () => intl.formatDate(new Date(`${date}T00:00:00`), { day: 'numeric', month: 'short' }),
    [intl, date],
  );

  let counts: ReactNode = null;

  if (day && attendance) {
    const values = { booked: day.present.length, total: attendance.totalInScope, count: bold };

    counts = isToday
      ? intl.formatMessage({ id: 'coworkersBookedToday' }, values)
      : intl.formatMessage({ id: 'coworkersBookedOnDate' }, { ...values, date: formattedDate });
  }

  return (
    <CoworkerGroupBannerBar data-testid="coworker-group-banner" role="status">
      <CoworkerGroupBannerText>
        {intl.formatMessage({ id: 'floorplanFilteredTo' }, { group: <strong>{group.name}</strong> })}
        {counts && <> · {counts}</>}
      </CoworkerGroupBannerText>
      <CoworkerGroupBannerClear type="button" data-testid="coworker-group-banner-clear" onClick={clearActiveGroup}>
        {intl.formatMessage({ id: 'clear' })}
      </CoworkerGroupBannerClear>
    </CoworkerGroupBannerBar>
  );
};
